/* ExposureCheckLedger.js */

"use strict";

/**
 * The check ledger: what was checked, what each check found, and WHAT WAS NEVER RUN.
 *
 * Up to five checks bear on a verdict and the drawer previously named only the winner. The last
 * line is the one that earns this section — a check that never ran is the only thing on the page an
 * operator can act on to change the outcome, and it was invisible.
 *
 * Derived entirely from the stored verdict; nothing here re-decides anything. `decidedAt` says which
 * rung set the severity, so every other rung is reported as context rather than as a competing
 * verdict.
 */

const ExposureDispositions = require("../../ExposureDispositions");
const ExposureConfigEvidence = require("./ExposureConfigEvidence");

/** Cost order: cheapest evidence first, device-touching last. Matches the scorer's own ladder. */
const LEVELS = ["platform", "feature", "version", "config", "runtime"];

const LEVEL_TITLES = {
    platform: "Hardware",
    feature: "Features",
    version: "Version",
    config: "Config",
    runtime: "Runtime",
};

/** Reason codes that mean the runtime check was never performed, and why. */
const RUNTIME_NOT_RUN = {
    RUNTIME_DEEPENING_NOT_ENABLED: "not run — runtime checks are off for this organization",
    RUNTIME_CHECK_UNMAPPED: "not run — the advisory publishes no determination command",
};

const CANNOT_CHECK_HARDWARE = {
    "hardware-unknown": "this device's hardware is not known — check inventory",
    undiscriminable: "the advisory's hardware list is too broad to compare",
};

function storedHardwareFinding(entry, check) {
    switch (check.result) {
        case "excluded":
            return {
                finding:
                    check.source === "cpe"
                        ? "the vendor's hardware scope excludes this device"
                        : "the advisory text does not cover this device",
                outcome: "cleared",
            };
        case "outside-named-scope":
            return {
                finding: "the advisory names hardware this device is not among",
                outcome: entry.decidedAt === "platform" ? "softened" : "did not decide",
            };
        case "covered":
            return {
                finding:
                    "this device's hardware is within the advisory's scope" +
                    (check.source === "cpe" ? " (vendor CPE data)" : " (advisory text)"),
                outcome: "in scope",
            };
        case "no-scope":
            return {
                finding: "the advisory names no hardware",
                outcome: "no hardware exclusion applies",
            };
        case "cannot-check":
            return {
                finding:
                    CANNOT_CHECK_HARDWARE[check.cause] ||
                    "the hardware check could not compare this device",
                outcome: "cannot verify",
            };
        default:
            return null;
    }
}

function hardwareFinding(entry) {
    const stored = entry.platformCheck ? storedHardwareFinding(entry, entry.platformCheck) : null;
    if (stored) {
        return stored;
    }
    if (entry.reasonCode === "NOT_APPLICABLE_PLATFORM") {
        return {finding: "the vendor's hardware scope excludes this device", outcome: "cleared"};
    }
    if (entry.reasonCode === "PLATFORM_SCOPE_MISMATCH") {
        return {
            finding: "the advisory names hardware this device is not among",
            outcome: "softened",
        };
    }
    return notRecorded(entry, "hardware");
}

/**
 * A rung the verdict says nothing about.
 *
 * The verdict carries ONE reasonCode. Exactly one rung can be described from it, and the rest were
 * previously given a positive default — so a device cleared at Hardware still read
 * "17.6.5 is within the advisory's affected releases" on the Version row, a finding no check
 * produced. That is the fabrication this file's own header disclaims when it says nothing here
 * re-decides anything.
 *
 * Deliberately NOT phrased as "not reached". Whether a rung ran is a property of the scorer's
 * evaluation order, which is not the display order above and is not something the verdict records;
 * guessing it here would replace one false statement with another. What IS certain is that the
 * verdict does not carry this rung's result, so that is what the row says.
 */
function notRecorded(entry, subject) {
    // Guard on decidedAt itself, not on the label: decidedAtLabel returns the em dash placeholder
    // for a missing value, which is truthy and would render "not recorded — decided on —".
    const decidedAt = entry && entry.decidedAt;
    return {
        finding: "no " + subject + " finding was recorded for this verdict",
        outcome: decidedAt
            ? "not recorded — decided on " + ExposureDispositions.decidedAtLabel(decidedAt)
            : "not recorded",
    };
}

function featureFinding(entry) {
    const matched = (entry.matchedTags || []).length;
    const evidence = entry.tagEvidence || {};
    if (matched > 0) {
        return {finding: (entry.matchedTags || []).join(", "), outcome: "configured"};
    }
    // notEvaluated FIRST, and it is not a variant of "not configured" — it is the absence of a
    // check. The backend routes every required tag here when the device's tag layer produced
    // nothing at all, which is a state a device can sit in for months without anyone noticing:
    // its verdicts all clear, and a cleared device is the one nobody opens. Reporting that as
    // "none of the required features are configured" would put a sentence about the device's
    // configuration on a row where the configuration was never read.
    if ((evidence.notEvaluated || []).length) {
        return {
            finding: "no feature tags were derived for this device",
            outcome: "cannot verify — not checked",
        };
    }
    if ((evidence.notObservable || []).length) {
        return {
            finding: "required features cannot be detected on this OS",
            outcome: "cannot verify",
        };
    }
    if ((entry.missingTags || []).length) {
        return {finding: "none of the required features are configured", outcome: "not configured"};
    }
    return {finding: "no feature precondition in the advisory", outcome: "nothing to check"};
}

/**
 * Reason codes that are themselves statements about the version gate.
 *
 * Only these license "in affected range". The previous default asserted it for EVERY code, so a
 * device cleared at Hardware or at the running config — rungs the scorer settles before the version
 * overlay is reached — still read "17.6.5 is within the advisory's affected releases" from a check
 * that never ran.
 *
 * Deliberately narrow. Several codes downstream of the version overlay (PARTIAL_TAG_MATCH,
 * CONFIG_MATCH_CONFIRMED, the runtime ones) do imply an in-range version, but establishing that
 * means encoding the scorer's evaluation order into the drawer, and that order is neither the
 * display order here nor anything the verdict records. Understating those rows is the safe
 * direction.
 */
const VERSION_GATE_REASONS = new Set([
    "VERSION_ONLY_MATCH",
    // The scorer's "version uncertain" yellow — the gate ran and could not settle the release.
    "CONFIG_ONLY_MATCH",
]);

const VERSION_SOURCE_LABELS = {
    cpe: " (CPE ranges)",
    predicate: " (advisory ranges)",
};

function storedVersionFinding(entry, check) {
    const version = entry.deviceVersionAtRun || "the device version";
    const sourceLabel = VERSION_SOURCE_LABELS[check.source] || "";
    switch (check.result) {
        case "in-range":
            if (check.source === "vendor-feed") {
                return {
                    finding: version + " is within the affected releases (vendor advisory feed)",
                    outcome: "in affected range",
                };
            }
            return {
                finding: version + " is within the advisory's affected releases" + sourceLabel,
                outcome: "in affected range",
            };
        case "out-of-range":
            return {
                finding: version + " is outside the affected releases" + sourceLabel,
                outcome: "outside affected range",
            };
        case "no-ranges":
            return {
                finding: entry.deviceVersionAtRun
                    ? entry.deviceVersionAtRun + " — the advisory lists no affected versions"
                    : "the advisory lists no affected versions",
                outcome: "nothing to check",
            };
        case "uncertain":
            return {
                finding: entry.deviceVersionAtRun
                    ? version + " could not be evaluated against the listed ranges"
                    : "device version unknown",
                outcome: "cannot verify",
            };
        case "disagreement":
            return {finding: "version sources disagree for " + version, outcome: "needs review"};
        default:
            return null;
    }
}

function versionFinding(entry) {
    const stored = entry.versionCheck ? storedVersionFinding(entry, entry.versionCheck) : null;
    if (stored) {
        return stored;
    }
    const version = entry.deviceVersionAtRun;
    if (!version) {
        return {finding: "device version unknown", outcome: "cannot verify"};
    }
    // "in affected range" / "outside affected range", NOT "affected" / "not affected". The outcome
    // column sits three lines under the panel's verdict badge, which uses that exact pair for the
    // whole device. Scanning a Not Affected device found the word "affected" in this column and read
    // it as a contradiction. This column reports what ONE dimension matched; only the badge speaks
    // for the device, so the two must not share a vocabulary.
    if (entry.reasonCode === "VERSION_OUT_OF_RANGE") {
        return {
            finding: version + " is outside the affected releases",
            outcome: "outside affected range",
        };
    }
    if (VERSION_GATE_REASONS.has(entry.reasonCode)) {
        return {
            finding: version + " is within the advisory's affected releases",
            outcome: "in affected range",
        };
    }
    // Everything else: the verdict was settled by another rung and says nothing about this one.
    // The device's version is a fact worth showing; what it matched is not ours to assert.
    return {
        finding: version,
        outcome: notRecorded(entry, "version").outcome,
    };
}

/**
 * Config row. Reports the check even when it changed nothing — that is the majority outcome, and
 * silence would read as "no such check exists" rather than "the check could not clear the device".
 */
function configFinding(entry) {
    const rows = entry.configEvidence;
    if (rows === null || rows === undefined) {
        return null;
    }
    if (!rows.length) {
        return {
            finding: "the advisory publishes no configuration check",
            outcome: "nothing to check",
        };
    }
    // Before any outcome is reported: with no captured config there was nothing to search, and
    // "nothing to check" would blame the advisory for a gap that belongs to the device.
    if (rows.every(ExposureConfigEvidence.isMissingSnapshot)) {
        return {
            finding: "no configuration snapshot — capture this device",
            outcome: "not checked",
        };
    }
    // ExposureConfigEvidence owns "did this check decide anything" — do not reimplement it. This
    // filter used to be a local copy, and when the scorer's clearance refusals started arriving on
    // the evidence the copy went stale: the ledger reported "found" for a check the scorer had
    // explicitly declined to act on, three inches above an evidence panel that correctly filed the
    // same row under the checks that could not clear.
    const decisive = rows.filter(ExposureConfigEvidence.isDecisive);
    const shown = (decisive.length ? decisive : rows)[0];
    const command = shown.command || shown.pattern || "";
    // Filtering by isDecisive is not enough on its own: with no decisive row the fallback shows
    // rows[0], and a scorer-refused row would then be described by its raw outcome — "explicitly
    // disabled" for a matcher the scorer declined to clear on. The refusal outranks the outcome,
    // because the outcome is what the pattern did and the refusal is whether it counted.
    if (shown.clearanceBlockedReason) {
        return {finding: command, outcome: "ran — could not clear this device"};
    }
    if (shown.outcome === "matched" && shown.meaning === "enabled") {
        return {finding: command, outcome: "found"};
    }
    if (shown.outcome === "matched" && shown.meaning === "disabled") {
        return {finding: command, outcome: "explicitly disabled"};
    }
    if (shown.outcome === "absent" && shown.sourceQuote) {
        return {finding: command, outcome: "not configured"};
    }
    if (shown.outcome === "invalid") {
        return {finding: command, outcome: "could not run"};
    }
    return {finding: command, outcome: "no match, absence unproven"};
}

/**
 * Runtime row, including the case that matters most: a check that exists and was never run.
 *
 * Returns `runnable: true` when collecting the evidence would be worth doing, which is what puts
 * the action on the row.
 */
function runtimeFinding(entry) {
    const evidence = entry.runtimeEvidence || [];
    if (evidence.length) {
        const active = evidence.filter(function (row) {
            return row.interpretation === "active";
        });
        const first = active.length ? active[0] : evidence[0];
        return {finding: first.command || "", outcome: first.interpretation || ""};
    }
    if (RUNTIME_NOT_RUN[entry.reasonCode]) {
        return {
            finding: "a device check would settle this",
            outcome: RUNTIME_NOT_RUN[entry.reasonCode],
            runnable: entry.reasonCode === "RUNTIME_DEEPENING_NOT_ENABLED",
        };
    }
    return null;
}

const BUILDERS = {
    platform: hardwareFinding,
    feature: featureFinding,
    version: versionFinding,
    config: configFinding,
    runtime: runtimeFinding,
};

/**
 * @returns {Array<{level, title, finding, outcome, decided, runnable}>} one row per check that has
 *   something to say. `decided` marks the rung that set the severity.
 */
function buildLedger(entry) {
    if (!entry) {
        return [];
    }
    const rows = [];
    LEVELS.forEach(function (level) {
        const built = BUILDERS[level](entry);
        if (!built) {
            return;
        }
        rows.push({
            level: level,
            title: LEVEL_TITLES[level],
            finding: built.finding,
            outcome: built.outcome,
            decided: entry.decidedAt === level,
            runnable: built.runnable === true,
        });
    });
    return rows;
}

/** Label for the deciding rung, for the summary line above the ledger. */
function decidedLabel(entry) {
    return ExposureDispositions.decidedAtLabel(entry && entry.decidedAt);
}

module.exports = {
    LEVELS,
    LEVEL_TITLES,
    buildLedger,
    decidedLabel,
};
